import { z } from 'zod';
import { FIRM_SIZE_VALUES } from './firm-size';
import { InvitableOrgRoleSchema, OrgRoleSchema } from './roles';
import { PRACTICE_AREA_IDS } from './practice-areas';
import { SenderIdentitySchema } from './sender-identity';

// ── Organization ────────────────────────────────────────────────────────────

const OrganizationSettingsSchema = z.object({
  allowMemberInvites: z.boolean(),
  requireEmailVerification: z.boolean(),
  defaultMemberRole: InvitableOrgRoleSchema,
  retentionDays: z.number().nullable(),
  enableAuditLog: z.boolean(),
  verificationsVisibleToMembers: z.boolean().nullish(),
  teamsEnabled: z.boolean().default(false),
  intake: z
    .object({
      intakeEmail: z.string().nullable().optional(),
      emailIntakeEnabled: z.boolean().optional(),
      includeSituations: z.boolean(),
      includeMatters: z.boolean(),
      includeQualifiers: z.boolean(),
      includeUrgency: z.boolean(),
    })
    .optional(),
});

export type OrganizationSettings = z.infer<typeof OrganizationSettingsSchema>;

const OrganizationLimitsSchema = z.object({
  maxMembers: z.number(),
  maxTeams: z.number(),
  maxInvitations: z.number(),
});

export type OrganizationLimits = z.infer<typeof OrganizationLimitsSchema>;

export const OrganizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  nameLowercase: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  website: z.string().nullish(),
  websiteNormalized: z.string().nullish(),
  stateOfIncorporation: z.string().nullish(),
  firmSize: z.enum(FIRM_SIZE_VALUES).nullish(),
  practiceAreas: z.array(z.enum(PRACTICE_AREA_IDS)).default([]),
  logoURL: z.string().nullable(),
  createdAt: z.string(),
  createdBy: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
  status: z.enum(['active', 'suspended', 'deleted']),
  settings: OrganizationSettingsSchema,
  limits: OrganizationLimitsSchema,
});

export type Organization = z.infer<typeof OrganizationSchema>;

// ── Organization Member ─────────────────────────────────────────────────────

export const OrganizationMemberSchema = z.object({
  userId: z.string(),
  email: z.string(),
  displayName: z.string().nullable(),
  photoURL: z.string().nullable(),
  role: OrgRoleSchema,
  joinedAt: z.string(),
  invitedBy: z.string().nullable(),
  status: z.enum(['active', 'invited', 'suspended']),
  permissions: z.array(z.string()),
  lastActiveAt: z.string(),
  preferences: z.object({
    emailNotifications: z.boolean(),
    desktopNotifications: z.boolean(),
  }),
  senderIdentity: SenderIdentitySchema.optional(),
});

export type OrganizationMember = z.infer<typeof OrganizationMemberSchema>;
